import React, {useCallback, useRef} from "react";
import {useSiren} from "../SirenContext";
import {useGetAudioFiles} from "../hooks/useGetAudioFiles";
import {AudioFile} from "../typedefs";
import {useNavigateWithClientID} from "../hooks/useNavigateWithClientID";
import {useAuth} from "../../../context/user";
import {Action, ActionSelect} from "../../../components/ui/ActionSelect";
import {usePlatformDeleteModal} from "../hooks/useDeleteModal";
import {UserName} from "../../../components/data/UserName";
import {PaginatedSelector} from "../../../components/paginated-selector/paginated-selector";
import {useCopyID} from "../../../lib/misc/useCopyID";

export function AudioList() {
    const {clientID} = useSiren()
    const {isSuperAdmin} = useAuth()
    const {navigateWithClientID} = useNavigateWithClientID()
    const getItems = useGetAudioFiles(clientID)
    const refetchRef = useRef<(() => void) | null>(null)

    const getColumns = useCallback((item: AudioFile) => [
        item.name,
        <UserName id={item.createdBy}/>,
        item.updatedBy ? <UserName id={item.updatedBy}/> : null,
    ], [])

    const renderActions = useCallback((item: AudioFile) => (
        <AudioActions item={item} refetch={() => refetchRef.current?.()}/>
    ), [])

    if (!clientID && isSuperAdmin) return null;

    return (
        <PaginatedSelector
            key={clientID}
            headers={["Name", "Created By", "Last Updated By"]}
            getItems={getItems}
            getColumns={getColumns}
            disableSelect={true}
            renderActions={renderActions}
            onColumnClick={(item) => navigateWithClientID(`siren/audio/${item.id}/edit`)}
            syncWithUrl={true}
            onRefetchReady={(refetch) => { refetchRef.current = refetch }}
        />
    )
}

function AudioActions({item, refetch}: { item: AudioFile; refetch: () => void }) {
    const {navigateWithClientID} = useNavigateWithClientID()
    const {copyID, isCopied} = useCopyID()

    const {DeleteModal, openDeleteModal} = usePlatformDeleteModal(
        "Audio File",
        item.name,
        `api/siren/audio-files/${item.id}`,
        refetch,
    )

    return (
        <>
            <DeleteModal/>
            <ActionSelect label="Actions">
                <Action label="Edit"
                        onSelect={() => navigateWithClientID(`siren/audio/${item.id}/edit`)}/>
                <Action label={isCopied(item.id) ? "Copied!" : "Copy ID"} onSelect={() => copyID(item.id)}/>
                <Action label="Delete" onSelect={openDeleteModal}/>
            </ActionSelect>
        </>
    )
}
